import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import Box from '@repo/ui/box'
import { ImageIcon, LinkIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import { useForm } from 'react-hook-form'

import { TripAddressInput } from '../../../lib/types/models/trip-address/trip-address-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledFileInput from '../../elements/inputs/controlled-file-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'
import ValidationButtons from '../../elements/validation-buttons'
import { tripAddressSchema } from './utils'

const TripAddressForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<TripAddressInput>) => {
  const form = useForm<TripAddressInput>({
    resolver: zodResolver(tripAddressSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="flex flex-col gap-4"
      >
        <Box>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-3">
              <InnerBox title="Détails du lieu de RDV">
                <div className="flex flex-col gap-3">
                  <FormField
                    control={form.control}
                    name="label"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput {...field} label="Titre" type="text" required />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="adresseURI"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput
                            {...field}
                            label="URL de l'adresse"
                            type="text"
                            autoCapitalize="off"
                            required
                            icon={(props) => (
                              <LinkIcon {...props} color={tailwindColors.primary.DEFAULT} />
                            )}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address1"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControl>
                          <ControlledAdressInput
                            {...field}
                            required
                            label="Adresse"
                            onDetailsChange={(placeDetails) => {
                              form.setValue('address1', placeDetails.address, {
                                shouldValidate: true
                              })
                              form.setValue('zipCode', placeDetails.postalCode, {
                                shouldValidate: true
                              })
                              form.setValue('city', placeDetails.city, { shouldValidate: true })
                              form.setValue('country', placeDetails.country, {
                                shouldValidate: true
                              })
                              form.trigger(['address1', 'zipCode', 'city', 'country'])
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address2"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput {...field} type="text" label="Complément d'adresse" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-3">
                    <FormField
                      control={form.control}
                      name="zipCode"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput {...field} type="text" label="Code postal" required />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput {...field} type="text" label="Ville" required />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput {...field} type="text" label="Pays" required />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </InnerBox>
            </div>
            <div>
              <InnerBox className="rounded-xl p-4">
                <div className="rounded-lg">
                  <FormField
                    control={form.control}
                    name="pictures"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledFileInput
                            multiple
                            previewOnClick
                            placeholder="Glisser-déposer ou parcourir"
                            types={[FileTypeEnum.IMAGE]}
                            icon={ImageIcon}
                            label={'Déposer des images'}
                            files={field?.value}
                            onFilesChange={(files) => {
                              form.setValue('pictures', files, { shouldValidate: true })
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </InnerBox>
            </div>
          </div>
          <div className="h-4" />
          <InnerBox title="Commentaires" titleColor={tailwindColors.grey.DEFAULT}>
            <FormField
              control={form.control}
              name={'comments'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledTextEditor {...field} placeholder={'Commentaires'} />
                  </FormControl>
                </FormItem>
              )}
            />
          </InnerBox>

          <ValidationButtons {...validationButtonProps} />
        </Box>
      </form>
    </Form>
  )
}

export default TripAddressForm
